<template>
    <LoadingScreen v-if="loading" message="Loading bucket list…" />
    <main v-else class="bucket-list-details">
        <div class="list-banner">
            <div class="overlay"></div>
            <h1 class="banner-title">{{ bucketList.name }}</h1>
        </div>
        <div class="max-width-container">
            <div class="user-data-wrapper">
                <div class="user-data-item type">
                    <span class="icon material-symbols-outlined">
                        {{ $iconGet("bucketListType", bucketList.type) }}
                    </span>
                    <div>
                        <span class="title">Bucket list type</span>
                        <div class="user-input">
                            {{ $enumGet("bucket_list_type", bucketList.type) }}
                        </div>
                    </div>
                </div>
                <div class="user-data-item status">
                    <span class="icon material-symbols-outlined">done</span>
                    <div class="single-text">
                        <span>{{ completedItems }} / {{ totalItems }}</span>
                    </div>
                </div>
            </div>
            <div class="actions">
                <router-link :to="`/bucket-lists/${id}/edit`">
                    <Bttn id="button-edit">Edit</Bttn>
                </router-link>
                <router-link to="/bucket-lists">
                    <Bttn id="button-back" class="outlined">Back</Bttn>
                </router-link>
                <Share
                    :item-id="id"
                    item-type="bucket_list"
                    :icon-only="false"
                    class="outlined"
                />
            </div>
            <div
                :class="[
                    'flex-container',
                    { custom: bucketList.type === 'custom' },
                ]"
            >
                <section ref="bliListWrapper" class="actions-and-list">
                    <div class="widget-wrapper">
                        <BLActionWidget
                            v-if="bucketList.items.length"
                            @sorting="onSorting"
                            @deleting="onDeleting"
                            @closing="onClosing"
                            @expanded="onToggleExpanded"
                        />
                        <SearchWidget
                            v-if="!actions.expanded"
                            v-model="searchString"
                        />
                    </div>
                    <div class="bli-list-wrapper">
                        <ul v-if="!actions.sorting">
                            <BLItem
                                v-for="item in filteredBLItems"
                                :key="item.id"
                                :bucket-list="bucketList"
                                :item="item"
                                :name="getName(item)"
                                :sorting="actions.sorting"
                                :deleting="actions.deleting"
                                :editing="actions.expanded"
                                @updated="refreshGeos"
                            />
                        </ul>
                        <template v-else>
                            <div class="sort-toolbar">
                                <div class="sort-toolbar-actions">
                                    <button
                                        class="sort-chip"
                                        @click="sortByField('name')"
                                    >
                                        <span class="material-symbols-outlined"
                                            >sort_by_alpha</span
                                        >
                                        By name
                                    </button>
                                    <button
                                        v-if="
                                            bucketList.type !== 'custom' &&
                                            bucketList.type !== 'country'
                                        "
                                        class="sort-chip"
                                        @click="sortByField('country')"
                                    >
                                        <span class="material-symbols-outlined"
                                            >flag</span
                                        >
                                        By country
                                    </button>
                                </div>
                                <span class="sort-save-hint">
                                    <span class="material-symbols-outlined"
                                        >info</span
                                    >
                                    Close to save
                                </span>
                            </div>
                            <draggable
                                :list="sortingData.items"
                                :disabled="!sortingData.enabled"
                                item-key="id"
                                class="bucket-list-sorting-array"
                                chosen-class="chosen"
                                ghost-class="ghost"
                                @start="toggleDrag(true)"
                                @end="toggleDrag(false)"
                            >
                                <template #item="{ element }">
                                    <BLItem
                                        :bucket-list="bucketList"
                                        :item="element"
                                        :name="getName(element)"
                                        sorting
                                    />
                                </template>
                            </draggable>
                        </template>
                        <BLItemForm
                            v-if="
                                !(
                                    actions.sorting ||
                                    actions.deleting ||
                                    actions.expanded
                                )
                            "
                            ref="addItemForm"
                            :bucket-list="bucketList"
                            :errors="errors"
                            @submit-form="submitItem"
                            @clear-error="clearError"
                        />
                        <!-- Recommendations Section -->
                        <div
                            v-if="
                                recommendations.length > 0 &&
                                !(
                                    actions.sorting ||
                                    actions.deleting ||
                                    actions.expanded
                                )
                            "
                            class="recommendations-container"
                        >
                            <h3 class="recommendations-title">
                                <span class="icon material-symbols-outlined"
                                    >auto_awesome</span
                                >
                                Recommended for you
                            </h3>
                            <ul class="recommendations-list">
                                <li
                                    v-for="(item, index) in recommendations"
                                    :key="index"
                                    :class="[
                                        'bucket-list-item recommendation',
                                        { wide: bucketList.type === 'custom' },
                                    ]"
                                >
                                    <div class="recommendation-content-wrapper">
                                        <div class="recommendation-main-info">
                                            <span
                                                class="recommendation-icon material-symbols-outlined"
                                                >auto_awesome</span
                                            >
                                            <div class="recommendation-details">
                                                <span class="label-text">
                                                    {{ getName(item)
                                                    }}<span
                                                        v-if="
                                                            bucketList.type !==
                                                                'country' &&
                                                            item.geo
                                                        "
                                                        class="label-subtext"
                                                        >,
                                                        {{
                                                            item.geo.country
                                                        }}</span
                                                    >
                                                </span>
                                                <span class="reason-text">{{
                                                    item.reason
                                                }}</span>
                                            </div>
                                        </div>
                                        <div class="recommendation-actions">
                                            <button
                                                class="approve-button"
                                                title="Add to list"
                                                @click="
                                                    submitRecommendation(
                                                        item,
                                                        index
                                                    )
                                                "
                                            >
                                                <span
                                                    class="material-symbols-outlined"
                                                    >add</span
                                                >
                                            </button>
                                            <button
                                                class="dismiss-button"
                                                title="Dismiss"
                                                @click="
                                                    dismissRecommendation(index)
                                                "
                                            >
                                                <span
                                                    class="material-symbols-outlined"
                                                    >close</span
                                                >
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <Bttn
                            v-if="
                                !(
                                    actions.sorting ||
                                    actions.deleting ||
                                    actions.expanded
                                )
                            "
                            class="slim outlined ai"
                            :class="{ fetching: fetchingRecommendations }"
                            :disabled="fetchingRecommendations"
                            :icon="
                                fetchingRecommendations
                                    ? 'sync'
                                    : 'auto_awesome'
                            "
                            @click="fetchRecommendations"
                        >
                            {{
                                fetchingRecommendations
                                    ? "Thinking..."
                                    : "Get AI recommendations"
                            }}
                        </Bttn>
                        <p
                            v-if="recommendationError"
                            class="recommendation-error"
                        >
                            {{ recommendationError }}
                        </p>
                    </div>
                </section>
                <section class="maps-section">
                    <div
                        v-if="regions.length"
                        :style="mapStyle"
                        class="region-map-wrapper"
                    >
                        <RegionMap :geos="regions" popup-on="click">
                            <template #popup="{ geos }">
                                <ul class="map-popup-card compact divided">
                                    <MapPopupCard
                                        v-for="geo in geos"
                                        :key="geo.getMeta().id"
                                        :title="geo.getMeta().name"
                                        :emoji="
                                            geo.getMeta().checked ? '✓' : '○'
                                        "
                                        clickable
                                        @title-click="
                                            goToItem(geo.getMeta().id)
                                        "
                                    />
                                </ul>
                            </template>
                        </RegionMap>
                    </div>
                    <div
                        v-if="points.length"
                        :style="mapStyle"
                        class="point-map-wrapper"
                    >
                        <PointMap :geos="points" popup-on="click">
                            <template #popup="{ geos }">
                                <ul class="map-popup-card compact divided">
                                    <MapPopupCard
                                        v-for="geo in geos"
                                        :key="geo.getMeta().id"
                                        :title="geo.getMeta().name"
                                        :emoji="
                                            geo.getMeta().checked ? '✓' : '○'
                                        "
                                        clickable
                                        @title-click="
                                            goToItem(geo.getMeta().id)
                                        "
                                    />
                                </ul>
                            </template>
                        </PointMap>
                    </div>
                    <MobileMapOverlay v-if="regions.length || points.length" />
                </section>
            </div>
        </div>
        <ConfirmModal
            v-if="pendingLeavePath !== null"
            title="Unsaved changes"
            message="You have unsaved changes, are you sure you want to leave?"
            confirm-label="Leave"
            cancel-label="Stay"
            @close="pendingLeavePath = null"
            @confirm="leaveConfirmedNavigation"
        />
    </main>
</template>

<script>
import { useBucketListStore } from "js/stores/bucketListStore";
import { useRecommendationStore } from "js/stores/recommendationStore";
import RegionMap from "js/components/maps/RegionMap";
import PointMap from "js/components/maps/PointMap";
import MapPopupCard from "js/components/maps/MapPopupCard";
import BLItem from "js/components/list-items/BLItem";
import BLItemForm from "js/components/forms/BLItemForm";
import BLActionWidget from "js/components/widgets/BLActionWidget";
import SearchWidget from "js/components/widgets/SearchWidget";
import BaseFormWrapper from "js/components/base/BaseFormWrapper";
import MobileMapOverlay from "js/components/blocks/MobileMapOverlay";
import LoadingScreen from "js/components/common/LoadingScreen";
import ConfirmModal from "js/components/modals/ConfirmModal";
import {
    Geo,
    getPlace,
    getCity,
    getCountry,
    getRegion,
} from "js/lib/geocoding.js";
import { ServerError, pageErrorFrom } from "js/lib/server.js";
import { usePageErrorStore } from "js/stores/pageErrorStore";
import draggable from "vuedraggable";

export default {
    name: "BucketListView",
    components: {
        RegionMap,
        PointMap,
        MapPopupCard,
        BLItem,
        BLItemForm,
        BLActionWidget,
        SearchWidget,
        MobileMapOverlay,
        LoadingScreen,
        ConfirmModal,
        draggable,
    },
    extends: BaseFormWrapper,
    inject: ["screenWidth"],
    beforeRouteLeave(to, from, next) {
        if (
            this.actions.sorting &&
            !this.sortingData.isPristine &&
            !this.leaveConfirmed
        ) {
            this.pendingLeavePath = to.fullPath;
            next(false);
            return;
        }

        next();
    },
    data() {
        return {
            id: null,
            loading: true,
            store: undefined,
            recommendationStore: undefined,
            bucketList: {},
            errors: {},
            regions: [],
            points: [],
            recommendations: [],
            fetchingRecommendations: false,
            recommendationError: null,
            needsReset: false,
            listXPos: 0,
            listYPos: 0,
            actions: {
                sorting: false,
                deleting: false,
                expanded: false,
            },
            pendingLeavePath: null,
            leaveConfirmed: false,
            sortingData: {
                items: [],
                enabled: true,
                isPristine: true,
            },
            showOverlayOnMobile: true,
            searchString: "",
        };
    },
    computed: {
        filteredBLItems() {
            if (this.searchString === "") {
                return this.bucketList.items;
            }

            return this.bucketList.items.filter((el) => {
                const name = this.getName(el).toLowerCase();
                const country = (el.geo.country ?? "").toLowerCase();
                const search = this.searchString.toLowerCase();

                return (name + country).includes(search);
            });
        },
        mapStyle() {
            const minTop = 100;
            this.screenWidth;

            if (
                this.screenWidth <= this.$breakpoint("md") ||
                this.listYPos > minTop
            ) {
                return {};
            }

            return {
                position: "fixed",
                left: `${this.listXPos + 380}px`,
                top: `${minTop}px`,
            };
        },
        completedItems() {
            return this.bucketList.items.reduce(
                (prev, item) => prev + (item.checked ? 1 : 0),
                0
            );
        },
        totalItems() {
            return this.bucketList.items.length;
        },
    },
    created() {
        window.addEventListener("scroll", this.onWindowChange);
        window.addEventListener("resize", this.onWindowChange);
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.onWindowChange);
        window.removeEventListener("resize", this.onWindowChange);
    },
    async mounted() {
        this.store = useBucketListStore();
        this.recommendationStore = useRecommendationStore();
        this.id = parseInt(this.$route.params.id);

        try {
            this.bucketList =
                this.store.find(this.id) ?? (await this.getBucketList());
        } catch (err) {
            usePageErrorStore().set(pageErrorFrom(err));
            this.loading = false;
            return;
        }
        this.refreshGeos();

        this.createSortableItems();
        this.loading = false;

        this.$nextTick(() => {
            this.onWindowChange();
        });
    },
    methods: {
        leaveConfirmedNavigation() {
            const path = this.pendingLeavePath;
            this.pendingLeavePath = null;
            this.leaveConfirmed = true;
            this.$router.push(path);
        },
        onWindowChange() {
            this.listYPos = this.$refs.bliListWrapper.getBoundingClientRect().y;
            this.listXPos = this.$refs.bliListWrapper.getBoundingClientRect().x;
        },
        async getBucketList() {
            const bl = await this.store.fetch(
                `/api/bucket-lists/${this.id}?with-items`
            );
            this.store.add(bl);
            return bl;
        },
        refreshGeos() {
            let regions = [];
            let points = [];
            this.bucketList.items.forEach((item) => {
                if (!item.geo) {
                    return;
                }
                const meta = {
                    id: item.id,
                    name: this.getName(item),
                    checked: item.checked,
                };
                if (this.bucketList.type === "country") {
                    regions.push(
                        new Geo(item.geo).setValue(item.checked).setMeta(meta)
                    );
                }
                if (
                    this.bucketList.type === "city" ||
                    this.bucketList.type === "place" ||
                    this.bucketList.type === "region"
                ) {
                    points.push(
                        new Geo(item.geo).setValue(item.checked).setMeta(meta)
                    );
                }
            });

            if (points.length) {
                this.points = points;
            }
            if (regions.length) {
                this.regions = regions;
            }
        },
        goToItem(id) {
            const row = document.getElementById(`item-${id}`);
            if (!row) {
                return;
            }
            row.scrollIntoView({ behavior: "smooth", block: "center" });
            row.classList.add("flash");
            setTimeout(() => row.classList.remove("flash"), 1600);
        },
        submitItem(data) {
            this.store
                .create(`/api/bucket-lists/${this.bucketList.id}/items/`, data)
                .then((item) => {
                    const form = this.$refs.addItemForm;
                    const keepTyping = form.$el.contains(
                        document.activeElement
                    );
                    this.store.find(this.bucketList.id).items.push(item);
                    this.clearErrors();
                    form.clear();
                    if (keepTyping) {
                        form.focusFirstField();
                    }
                    this.refreshGeos();
                })
                .catch(this.handleResponseErrors);
        },
        getName(item) {
            if (
                this.bucketList.type === "custom" ||
                this.bucketList.type === "place"
            ) {
                return item.name;
            }

            if (this.bucketList.type === "country") {
                return item.geo.name ?? item.geo.country;
            }

            return item.geo.name;
        },
        onSorting() {
            this.createSortableItems();
            this.actions.sorting = true;
            this.actions.deleting = false;
        },
        async onDeleting() {
            if (this.actions.sorting) {
                await this.updateSortOrder();
            }

            this.actions.deleting = true;
        },
        async onClosing() {
            if (this.actions.sorting) {
                await this.updateSortOrder();
            }

            this.actions.deleting = false;
        },
        onToggleExpanded(event) {
            this.actions.expanded = event;
        },
        createSortableItems() {
            this.sortingData.items = this.bucketList.items.map((el) => el);
        },
        async updateSortOrder() {
            let order = this.sortingData.items.map((el) => {
                return el.id;
            });

            this.store
                .edit(`/api/bucket-lists/${this.bucketList.id}?with-items`, {
                    item_order: order,
                })
                .then((response) => {
                    this.store.update(response);
                    this.actions.sorting = false;
                    this.sortingData.isPristine = true;
                    this.bucketList = response;
                })
                .catch(this.handleResponseErrors);
        },
        toggleDrag(state) {
            this.sortingData.isPristine = false;
            this.draggging = state;
        },
        sortByField(field) {
            this.sortingData.items.sort((a, b) => {
                let valA, valB;
                if (field === "country") {
                    valA = (a.geo?.country ?? "").toLowerCase();
                    valB = (b.geo?.country ?? "").toLowerCase();
                } else {
                    valA = this.getName(a).toLowerCase();
                    valB = this.getName(b).toLowerCase();
                }
                return valA.localeCompare(valB);
            });
            this.sortingData.isPristine = false;
        },
        fetchRecommendations() {
            this.fetchingRecommendations = true;
            this.recommendationError = null;
            this.recommendations = [];
            const reset = this.needsReset;
            this.needsReset = false;

            this.recommendationStore
                .loadBucketListIdeas(this.bucketList.id, reset)
                .then((suggestions) => {
                    this.recommendations = [];
                    const promises = suggestions.map(async (rec) => {
                        let geo = null;
                        try {
                            switch (this.bucketList.type) {
                                case "custom":
                                    geo = null;
                                    break;
                                case "country":
                                    geo = await getCountry(rec.name);
                                    break;
                                case "region":
                                    geo = await getRegion(
                                        rec.region,
                                        rec.country
                                    );
                                    break;
                                case "city":
                                    geo = await getCity(rec.city, rec.country);
                                    break;
                                case "place":
                                    geo = await getPlace(
                                        rec.place,
                                        rec.country
                                    );
                                    break;
                            }
                        } catch (err) {
                            if (
                                err instanceof ServerError &&
                                err.status === 429
                            ) {
                                this.recommendationError = err.message;
                                return;
                            }
                            throw err;
                        }
                        if (geo === null && this.bucketList.type !== "custom") {
                            return;
                        }
                        if (geo?.country_code) {
                            geo.country = this.$enumGet(
                                "country",
                                geo.country_code
                            );
                        }
                        this.recommendations.push({
                            name:
                                this.bucketList.type === "place" ||
                                this.bucketList.type === "custom"
                                    ? rec.name
                                    : null,
                            reason: rec.reason,
                            geo: geo === null ? undefined : geo,
                        });
                    });

                    Promise.all(promises).finally(() => {
                        this.fetchingRecommendations = false;
                    });
                })
                .catch((err) => {
                    if (err instanceof ServerError && err.status === 429) {
                        this.recommendationError =
                            err.data?.error === "ai_budget_exhausted"
                                ? err.data.message
                                : err.message;
                    } else {
                        this.recommendationError =
                            "Failed to fetch recommendations. Please try again.";
                    }
                    this.fetchingRecommendations = false;
                });
        },
        submitRecommendation(item, index) {
            this.store
                .create(`/api/bucket-lists/${this.bucketList.id}/items/`, item)
                .then((newBli) => {
                    this.store.find(this.bucketList.id).items.push(newBli);
                    this.clearErrors();
                    this.refreshGeos();
                    this.recommendations.splice(index, 1);
                    this.needsReset = true;
                })
                .catch(this.handleResponseErrors);
        },
        dismissRecommendation(index) {
            this.recommendations.splice(index, 1);
            this.needsReset = true;
        },
    },
};
</script>
